import {
    TelemetryChannel,
    TelemetryType,
} from '@tableau/microview-communication-service/lib/src/channelDefinitions/TelemetryChannelDefinitions';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~src/commonInfra/services/Telemetry';
import { ExplorerEventContentType } from '~src/commonInfra/telemetryEvents/TelemetryEventCreator';
import { ExplorerSortEvent, SearchEPTEvent, SearchFiltersUpdatedEvent, SearchImpressionEvent, SearchInitiatedEvent, SearchNoResultEvent, SearchResultClickedEvent, SearchTermUpdatedEvent, TelemetryEvents } from '~src/commonInfra/telemetryEvents/TelemetryEvents';
import { FiltersEvents, FiltersUpdatedEvent } from '~src/spacesPlatform/components/Explorer/helpers/FilterUpdater';
import { SubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { searchTelemetry } from '../components/SearchTelemetry';

export function startListening(): () => void {
    const telemetryEvent = new TelemetryChannel();

    const stopListeningToTelemetryEventChannel = telemetryEvent.listen((r) => {
        switch (r.type) {
            case TelemetryType.ClickSearchResult:
                return sendSearchResultClickedEvent(r.bag as Omit<SearchResultClickedEvent, 'searchFlowId'>, r.skipClearSearchFlowId);
            case TelemetryType.ClickSearchResultDirectlyToTelemetryService:
                return sendSearchResultDirectlyToTelemetryServiceClickedEvent(r.bag as SearchResultClickedEvent);
            case TelemetryType.FilterSearch:
                return sendFiltersUpdatedEvent(r.bag as FilterTelemetryEvent);
            case TelemetryType.SortSearch:
                return sendSortEvent(r.bag as SortTelemetryEvent);
            case TelemetryType.FilterSearch:
                return sendSearchInitiatedEvent(r.bag as SearchInitiatedEvent);
            case TelemetryType.SearchFiltersUpdated:
                return sendSearchFiltersUpdatedEvent(r.bag as SearchFiltersUpdatedEvent);
            case TelemetryType.SearchTermUpdate:
                return sendSearchTermUpdatedEvent(r.bag as SearchTermUpdatedEvent);
            case TelemetryType.SearchEPT:
                return sendSearchEPTEvent(r.bag as SearchEPTEvent);
            case TelemetryType.SearchNoResult:
                return sendSearchNoResultEvent(r.bag as SearchNoResultEvent);
            case TelemetryType.SearchImpression:
                return sendSearchImpressionEvent(r.bag as SearchImpressionEvent);
            default:
                return null;
        }
    });

    const sendSearchResultClickedEvent = (event: Omit<SearchResultClickedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) => {
        const result: Omit<SearchResultClickedEvent, 'searchFlowId'> = {
            type: TelemetryEvents.SearchResultClicked,
            eventSource: event.eventSource,
            resultType: event.resultType,
            seeAll: event.seeAll,
            index: event.index,
            total: event.total,
            searchTerm: event.searchTerm,
            contentId: event.contentId,
        };
        if (skipClearSearchFlowId){
            return searchTelemetry.sendSearchResultClickedEvent(result, skipClearSearchFlowId);}
        return searchTelemetry.sendSearchResultClickedEvent(result);
    };

    const sendFiltersUpdatedEvent = (event: FilterTelemetryEvent) => {
        if (FeatureFlags.searchProductMetrics) {
            const result: SearchFiltersUpdatedEvent = {
                displayMode: undefined,
                type: TelemetryEvents.SearchFiltersUpdated,
                content_type: event.contentType as ExplorerEventContentType,
                search_term: (event as any)['searchTerm'],
                ...(event.added) && { filters_added: event.added },
                ...(event.removed) && { filters_removed: event.removed },
                ...(event.updated) && { filters_updated: event.updated },
            };
            return searchTelemetry.sendSearchFiltersUpdatedEvent(result);
        }
        const telemetryEvent: FiltersUpdatedEvent = {
            displayMode: undefined,
            type: FiltersEvents.FiltersUpdated,
            space: 'search',
            subspace: event.pageType as SubspaceName, //single or mixed page type
            contentType: event.contentType as ExplorerEventContentType,
            ...(event.added) && { filtersAdded: event.added },
            ...(event.removed) && { filtersRemoved: event.removed },
            ...(event.updated) && { filtersUpdated: event.updated },
        };
        return Telemetry.sendEvent<FiltersUpdatedEvent>(telemetryEvent);
    };

    const sendSortEvent = (event: SortTelemetryEvent) => {
        const telemetryEvent: ExplorerSortEvent = {
            type: TelemetryEvents.ExplorerSort,
            space: 'search',
            subspace: event.pageType, //single or mixed page type
            contentType: event.contentType,
            sortField: event.field,
            ascending: event.ascending,
            eventSource: event.eventSource,
        };
        return Telemetry.sendEvent<ExplorerSortEvent>(telemetryEvent);
    };

    const sendSearchResultDirectlyToTelemetryServiceClickedEvent = (event: SearchResultClickedEvent) => {
        return Telemetry.sendEvent<SearchResultClickedEvent>(event);
    };

    const sendSearchInitiatedEvent = (event: SearchInitiatedEvent) => {
        return Telemetry.sendEvent<SearchInitiatedEvent>(event);
    };

    const sendSearchFiltersUpdatedEvent = (event: SearchFiltersUpdatedEvent) => {
        return Telemetry.sendEvent<SearchFiltersUpdatedEvent>(event);
    };

    const sendSearchTermUpdatedEvent = (event: SearchTermUpdatedEvent) => {
        return Telemetry.sendEvent<SearchTermUpdatedEvent>(event);
    };

    const sendSearchEPTEvent = (event: SearchEPTEvent) => {
        return Telemetry.sendEvent<SearchEPTEvent>(event);
    };

    const sendSearchNoResultEvent = (event: SearchNoResultEvent) => {
        return Telemetry.sendEvent<SearchNoResultEvent>(event);
    };

    const sendSearchImpressionEvent = (event: SearchImpressionEvent) => {
        return Telemetry.sendEvent<SearchImpressionEvent>(event);
    };

    return () => {
        stopListeningToTelemetryEventChannel();
    };
}
interface FilterTelemetryEvent {
    pageType: string;
    contentType: string;
    added?: string;
    removed?: string;
    updated?: string;
}
interface SortTelemetryEvent {
    pageType: string;
    contentType: string;
    field: string;
    ascending: boolean;
    eventSource: 'column' | 'dropdown';
}
